import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { type z } from 'zod';

import { isDefined, useDebounce } from 'common';

import {
  NodeConfigTab,
  type ReactFlowBlockTypeData,
  ReactFlowBlockTypeDataObjectWithoutConfigSchema,
} from '../../types';

export async function getBlockNodeErrorResults(args: {
  nodeConfig: Record<string, unknown> | undefined;
  nodeData: ReactFlowBlockTypeData | undefined;
  schema: z.ZodSchema;
}) {
  const { nodeConfig, nodeData, schema } = args;
  const nodeConfigResult = await schema.safeParseAsync(nodeConfig);
  const nodeDataResult = ReactFlowBlockTypeDataObjectWithoutConfigSchema.safeParse(nodeData);
  const nodeHasErrors = !nodeConfigResult.success || !nodeDataResult.success;
  return { nodeConfigResult, nodeDataResult, nodeHasErrors };
}

const BLOCK_CONFIG_ERROR_TAB_TO_FIELDS: Record<NodeConfigTab, Set<string | number>> = {
  [NodeConfigTab.Configuration]: new Set([
    'label',
    'description',
    'config',
    'mockData',
    'mockDataEnabled',
  ]),
  [NodeConfigTab.Analytics]: new Set(['events', 'stats', 'milestoneName']),
  [NodeConfigTab.Documentation]: new Set([]),
  [NodeConfigTab.LastTestRunOutput]: new Set([]),
  /** Only used when resourceFirstArchitectureDemo flag is on */
  [NodeConfigTab.Resources]: new Set([]),
};

const NO_ERRORS_BY_TAB: Record<NodeConfigTab, string[]> = {
  [NodeConfigTab.Configuration]: [],
  [NodeConfigTab.Analytics]: [],
  [NodeConfigTab.Documentation]: [],
  [NodeConfigTab.LastTestRunOutput]: [],
  /** Only used when resourceFirstArchitectureDemo flag is on */
  [NodeConfigTab.Resources]: [],
};

/**
 * react-query hashes the queryKey by deep-serializing it (JSON.stringify). A zod
 * schema serializes to megabytes in v4 (the HumanTask schema is ~17MB), so
 * including the schema object in the key makes the per-render hash cost ~1s for
 * large schemas. We only need identity (shallow) equality for the schema, so map
 * each schema instance to a stable primitive token via a WeakMap and key on that.
 */
const schemaIdentityKeys = new WeakMap<object, string>();
let nextSchemaIdentityKey = 0;
function getSchemaIdentityKey(schema: z.ZodSchema | undefined): string | undefined {
  if (!isDefined(schema)) {
    return undefined;
  }
  let key = schemaIdentityKeys.get(schema);
  if (!isDefined(key)) {
    key = `schema-${nextSchemaIdentityKey}`;
    schemaIdentityKeys.set(schema, key);
    nextSchemaIdentityKey += 1;
    // Block config schemas are static module constants (~one per block type), so
    // this count should plateau in the low hundreds. Unbounded growth means
    // schemas are being created dynamically, which defeats the identity key.
    if (nextSchemaIdentityKey > 10_000) {
      console.warn(
        'useBlockConfigValidation: generated over 10,000 schema identity keys, which likely indicates dynamically created schemas rather than static module constants.'
      );
    }
  }
  return key;
}

interface UseBlockConfigValidationProps {
  schema: z.ZodSchema | undefined;
  nodeConfig: Record<string, unknown> | undefined;
  nodeData: ReactFlowBlockTypeData | undefined;
  nodeId: string | undefined;
  enabled?: boolean;
}

/**
 * This hook validates the block configuration and data against the provided schema.
 * It returns the error messages, whether the schema validates, and whether the configuration and analytics fields validate separately.
 * It debounces the validation to help avoid performance issues when typing in the configuration fields.
 *
 * This hook can cause significant performance issues, depending on changes.
 * To evaluate the performance of changes, use React Profiler and filter by "useBlockConfigValidation".
 * Also, create a flow with a large number of blocks, and test different block configurations.
 * Test the analytics tab as well as the configuration tab, since they validate different fields and can have different performance characteristics.
 *
 * This can cause surprising performance issues in production, which can be hard to identify in development.
 */
export function useBlockConfigValidation(props: UseBlockConfigValidationProps) {
  const { schema, nodeConfig, nodeData, nodeId, enabled = true } = props;

  const queryProps = useMemo(
    () => ({
      schema,
      nodeId,
      nodeConfig,
      nodeData,
    }),
    [schema, nodeId, nodeConfig, nodeData]
  );
  const {
    schema: debouncedSchema,
    nodeId: debouncedNodeId,
    nodeConfig: debouncedConfig,
    nodeData: debouncedNodeData,
  } = useDebounce(queryProps, 300);
  const validationResponse = useQuery({
    queryKey: [
      'FlowBuilder:ReactFlow:NodeCard:SchemaValidation',
      enabled,
      getSchemaIdentityKey(debouncedSchema),
      /**
       * The validation needs to be tied to the id, since there can be a delay when switching between nodes in the UI.
       * The node ID is included in the query key to ensure that the validation is tied to the specific node being edited.
       * This ensures we always show the error for the selected node, even if it has the same schema as another node.
       */
      debouncedNodeId,
      debouncedConfig,
      debouncedNodeData,
    ],
    queryFn: async () => {
      if (!enabled || !isDefined(debouncedSchema)) {
        return { errorMessages: [], errorMessagesByTab: NO_ERRORS_BY_TAB };
      }
      try {
        /**
         * If a schema has an async refine, we need to use safeParseAsync
         * to get the correct validation result.
         * Example:
         * z.string().refine(async () => true).safeParse('test'); // throws
         * z.string().refine(async () => true).safeParseAsync('test'); // returns { success: true }
         */
        const { nodeConfigResult, nodeDataResult } = await getBlockNodeErrorResults({
          nodeConfig: debouncedConfig,
          nodeData: debouncedNodeData,
          schema: debouncedSchema,
        });
        const errorMessages: string[] = [];
        const errorMessagesByTab: Record<NodeConfigTab, string[]> = {
          [NodeConfigTab.Configuration]: [],
          [NodeConfigTab.Analytics]: [],
          [NodeConfigTab.Documentation]: [],
          [NodeConfigTab.LastTestRunOutput]: [],
          [NodeConfigTab.Resources]: [], // Only used when resourceFirstArchitectureDemo flag is on
        };
        if (!nodeConfigResult.success) {
          errorMessages.push(...nodeConfigResult.error.issues.map((error) => error.message));
          for (const error of nodeConfigResult.error.issues) {
            errorMessagesByTab[NodeConfigTab.Configuration].push(error.message);
          }
        }
        if (!nodeDataResult.success) {
          errorMessages.push(...nodeDataResult.error.issues.map((error) => error.message));
          for (const error of nodeDataResult.error.issues) {
            const topLevelPath = error.path.at(0);
            Object.values(NodeConfigTab).forEach((errorTab) => {
              if (
                (typeof topLevelPath === 'string' || typeof topLevelPath === 'number') &&
                BLOCK_CONFIG_ERROR_TAB_TO_FIELDS[errorTab].has(topLevelPath)
              ) {
                errorMessagesByTab[errorTab].push(error.message);
              }
            });
          }
        }
        return { errorMessages, errorMessagesByTab };
      } catch (error) {
        console.error(error);
        return { errorMessages: [], errorMessagesByTab: NO_ERRORS_BY_TAB };
      }
    },
    enabled: enabled && isDefined(debouncedSchema),
    staleTime: Infinity,
    retry: 0,
  });

  if (nodeId !== debouncedNodeId || !isDefined(validationResponse.data)) {
    return {
      errorMessages: [],
      errorMessagesByTab: NO_ERRORS_BY_TAB,
      doesSchemaValidate: true,
    };
  }
  return {
    errorMessages: validationResponse.data.errorMessages,
    errorMessagesByTab: validationResponse.data.errorMessagesByTab,
    doesSchemaValidate: validationResponse.data.errorMessages.length === 0,
  };
}
